import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText, Upload } from 'lucide-react';
import api from '@/lib/api';
import TelaProcessando from '@/components/TelaProcessando';
import { reais } from './tipos';

type Previa = {
  arquivo: string;
  competencia: string;
  emissao: string | null;
  ja_importada: boolean;
  novidades: { setores: { codigo: string; nome: string }[]; rubricas: { codigo: string; descricao: string }[] };
  setores: { codigo: string; nome: string; rubricas: number; proventos: number; descontos: number; funcionarios: number }[];
  empresa: { proventos: number; descontos: number; liquido: number; funcionarios: number; rubricas: number };
};

function mensagemDoErro(erro: unknown): string {
  const dados = (erro as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data;

  return dados?.errors ? Object.values(dados.errors)[0][0] : (dados?.message ?? 'Não foi possível ler o arquivo.');
}

/**
 * O PDF da folha, em dois passos: primeiro o sistema mostra o que entendeu —
 * setor por setor, com os totais — e só depois de conferido a competência é
 * gravada. É o mesmo cuidado da importação da cotação.
 */
export default function ImportarFolhaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [arrastando, setArrastando] = useState(false);

  const previa = useMutation({
    mutationFn: async (escolhido: File) => {
      const corpo = new FormData();
      corpo.append('arquivo', escolhido);

      return (await api.post<{ data: Previa; message: string }>('/folhas/previa', corpo)).data;
    },
  });

  const importar = useMutation({
    mutationFn: async (escolhido: File) => {
      const corpo = new FormData();
      corpo.append('arquivo', escolhido);

      return (await api.post<{ data: { id: number }; pendencias: string[] }>('/folhas', corpo)).data;
    },
    onSuccess: (resposta) => {
      void queryClient.invalidateQueries({ queryKey: ['folhas'] });
      navigate(`/folha/${resposta.data.id}`);
    },
  });

  function escolher(escolhido: File | undefined) {
    if (!escolhido) {
      return;
    }

    setArquivo(escolhido);
    previa.mutate(escolhido);
  }

  if (importar.isPending) {
    return <TelaProcessando titulo="Gravando a folha" detalhe="Setores, rubricas e impostos do mês." />;
  }

  const lida = previa.data?.data;

  return (
    <div className="px-lg py-lg">
      <header className="mb-lg">
        <h1 className="text-[22px] font-bold text-ink">Importar a folha do mês</h1>
        <p className="mt-xs text-[13px] text-muted">
          O resumo por setor em PDF, como o escritório envia. Os relatórios de imposto entram depois.
        </p>
      </header>

      <div
        onDragOver={(evento) => {
          evento.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(evento) => {
          evento.preventDefault();
          setArrastando(false);
          escolher(evento.dataTransfer.files[0]);
        }}
        className={`rounded-lg border-2 border-dashed px-lg py-xl text-center transition-colors ${
          arrastando ? 'border-accent bg-accent/5' : 'border-border bg-surface'
        }`}
      >
        <FileText size={28} className="mx-auto text-muted" />
        <p className="mt-md text-[15px] font-bold text-ink">{arquivo ? arquivo.name : 'Arraste aqui o PDF da folha'}</p>
        <p className="mt-xs text-[13px] text-muted">ou</p>
        <button
          type="button"
          onClick={() => entrada.current?.click()}
          className="mt-sm inline-flex h-9 items-center gap-sm rounded-lg border border-border px-md text-[13px] font-bold text-ink hover:bg-surface-subtle"
        >
          <Upload size={15} />
          Escolher o arquivo
        </button>
        <input ref={entrada} type="file" accept=".pdf" className="hidden" onChange={(evento) => escolher(evento.target.files?.[0])} />
      </div>

      {previa.isPending && <p className="mt-md text-[13px] text-muted">Lendo o arquivo…</p>}

      {previa.isError && (
        <p className="mt-md flex items-center gap-sm rounded-lg bg-danger/10 px-md py-sm text-[13px] text-danger">
          <AlertTriangle size={16} />
          {mensagemDoErro(previa.error)}
        </p>
      )}

      {lida && (
        <section className="mt-lg rounded-lg border border-border bg-surface">
          <header className="flex flex-wrap items-center justify-between gap-md border-b border-border px-md py-sm">
            <div>
              <p className="text-[15px] font-bold text-ink">Competência {lida.competencia.replace('-', '/')}</p>
              <p className="text-[12px] text-muted">
                {lida.setores.length} setores · {lida.empresa.rubricas} rubricas · {lida.empresa.funcionarios} funcionários
                {lida.emissao ? ` · emitida em ${lida.emissao.split('-').reverse().join('/')}` : ''}
              </p>
            </div>

            <button
              type="button"
              disabled={!arquivo}
              onClick={() => arquivo && importar.mutate(arquivo)}
              className="h-10 rounded-lg bg-accent px-md text-[14px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {lida.ja_importada ? 'Substituir a folha desta competência' : 'Confirmar e gravar'}
            </button>
          </header>

          {lida.ja_importada && (
            <p className="flex items-center gap-sm border-b border-border bg-warning/10 px-md py-sm text-[13px] text-warning">
              <AlertTriangle size={16} />
              Esta competência já foi importada. Gravar de novo refaz o que veio do PDF e mantém os relatórios de imposto.
            </p>
          )}

          {(lida.novidades.setores.length > 0 || lida.novidades.rubricas.length > 0) && (
            <div className="border-b border-border bg-warning/10 px-md py-sm text-[13px] text-ink">
              <p className="flex items-center gap-sm font-bold">
                <AlertTriangle size={16} className="text-warning" />
                Esta folha traz o que os cadastros ainda não conhecem
              </p>
              <p className="mt-xs pl-[24px] text-muted">Dá para gravar agora; a planilha do ERP só sai depois de cadastrar.</p>
              <ul className="mt-xs space-y-[2px] pl-[24px]">
                {lida.novidades.setores.map((setor) => (
                  <li key={setor.codigo}>
                    Setor <strong>{setor.codigo} {setor.nome}</strong> sem centro de custo —{' '}
                    <Link to="/centros-de-custo" className="font-bold text-accent hover:underline">
                      vincular em Centros de custo
                    </Link>
                  </li>
                ))}
                {lida.novidades.rubricas.map((rubrica) => (
                  <li key={rubrica.codigo}>
                    Rubrica <strong>{rubrica.codigo} {rubrica.descricao}</strong> sem verba —{' '}
                    <Link to="/rubricas-e-verbas" className="font-bold text-accent hover:underline">
                      classificar em Rubricas e verbas
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <table className="w-full text-[13px]">
            <thead className="border-b-2 border-border bg-surface-subtle text-[11px] tracking-wide text-ink-soft uppercase">
              <tr>
                <th className="px-sm py-sm text-left font-bold">Setor</th>
                <th className="px-sm py-sm text-right font-bold">Rubricas</th>
                <th className="px-sm py-sm text-right font-bold">Funcionários</th>
                <th className="px-sm py-sm text-right font-bold">Proventos</th>
                <th className="px-sm py-sm text-right font-bold">Descontos</th>
              </tr>
            </thead>
            <tbody>
              {lida.setores.map((setor) => (
                <tr key={setor.codigo} className="border-b border-border last:border-0 even:bg-dominant">
                  <td className="px-sm py-xs">
                    <span className="font-bold text-ink">{setor.nome}</span>
                    <span className="ml-xs text-[11px] text-muted">{setor.codigo}</span>
                  </td>
                  <td className="px-sm py-xs text-right tabular-nums">{setor.rubricas}</td>
                  <td className="px-sm py-xs text-right tabular-nums">{setor.funcionarios}</td>
                  <td className="px-sm py-xs text-right tabular-nums">{reais.format(setor.proventos)}</td>
                  <td className="px-sm py-xs text-right tabular-nums">{reais.format(setor.descontos)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-border bg-surface-subtle font-bold">
                <td className="px-sm py-sm text-ink">Resumo geral da empresa</td>
                <td className="px-sm py-sm text-right tabular-nums">{lida.empresa.rubricas}</td>
                <td className="px-sm py-sm text-right tabular-nums">{lida.empresa.funcionarios}</td>
                <td className="px-sm py-sm text-right tabular-nums">{reais.format(lida.empresa.proventos)}</td>
                <td className="px-sm py-sm text-right tabular-nums">{reais.format(lida.empresa.descontos)}</td>
              </tr>
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
